// PLAN-cockpit-v2-domain-lenses Phase 4: Mind Lens
// Sub-sidebar: 直近 10 件 Emotion Log 一覧
// Main: Emotion Log 入力フォーム + mind entries タイムライン
// v1 MindDashboardCard から移植、v2 デザイントークン適用

import { useEffect, useState } from 'react'
import { listOperationalData, upsertOperationalData } from '../../api/kdb'
import { apiFetch } from '../../api/client'
import { ResultRow } from '../components/common/ResultRow'
import { EmptyState } from '../components/common/EmptyState'
import { useSelectedItem } from '../hooks/useSelectedItem'
import { useViewport } from '../hooks/useViewport'
import type { SearchHit, SearchResponse } from '../../types'

interface MindEmotionLog {
  log_id: string
  observed_at: string
  emotion: string
  intensity: number | null
  trigger: string | null
  body_sensation: string | null
  thought: string | null
  context: string | null
  source: string | null
  ingested_at: string
  task_id: string | null
}

const DATASET = 'mind_emotion_log'

const EMOTIONS = [
  'joy', 'anger', 'fear', 'sadness', 'shame', 'pride',
  'gratitude', 'frustration', 'calm', 'anxiety', 'other',
] as const

const NEGATIVE_EMOTIONS = new Set(['anger', 'fear', 'sadness', 'shame', 'frustration', 'anxiety'])
const POSITIVE_EMOTIONS = new Set(['joy', 'pride', 'gratitude', 'calm'])

function nowLocal(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
}

function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function fmtDateTime(iso: string): string {
  try { return new Date(iso).toLocaleString('ja-JP', { hour12: false, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }
  catch { return iso }
}

function emotionColor(emotion: string): string {
  if (NEGATIVE_EMOTIONS.has(emotion)) return 'bg-state-error/10 text-state-error'
  if (POSITIVE_EMOTIONS.has(emotion)) return 'bg-state-success/10 text-state-success'
  return 'bg-bg-overlay text-text-subtle'
}

// ===== Sub-sidebar =====

export function MindSidebar() {
  const [logs, setLogs] = useState<MindEmotionLog[]>([])
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let cancelled = false
    listOperationalData<MindEmotionLog>(DATASET, { limit: 10 })
      .then(res => { if (!cancelled) setLogs(res.rows) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [reload])

  return (
    <div className="p-4 space-y-4 text-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-wide text-text-subtle">Recent Logs</h2>
        <button type="button" onClick={() => setReload(n => n + 1)}
          className="text-xs text-text-subtle hover:text-text-base">↺</button>
      </div>
      <div className="space-y-2">
        {logs.length === 0 && <p className="text-xs text-text-muted">記録なし</p>}
        {logs.map(row => (
          <div key={row.log_id} className="text-xs border-b border-border-subtle pb-2">
            <div className="flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${emotionColor(row.emotion)}`}>
                {row.emotion}
              </span>
              {row.intensity != null && <span className="text-text-subtle">{row.intensity}/10</span>}
            </div>
            <span className="text-text-subtle font-mono">{fmtDateTime(row.observed_at)}</span>
            {row.trigger && <p className="text-text-muted mt-0.5 line-clamp-1">{row.trigger}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}

// ===== Main Lens =====

export function MindLens() {
  const { setSelectedItem, selectedItem } = useSelectedItem()
  const viewport = useViewport()
  const [reload, setReload] = useState(0)
  const [showDetails, setShowDetails] = useState(false)

  // form state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [observedAt, setObservedAt] = useState(nowLocal)
  const [emotion, setEmotion] = useState('calm')
  const [intensity, setIntensity] = useState('5')
  const [trigger, setTrigger] = useState('')
  const [bodySensation, setBodySensation] = useState('')
  const [thought, setThought] = useState('')
  const [context, setContext] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitMsg, setSubmitMsg] = useState<string | null>(null)

  // timeline
  const [timeline, setTimeline] = useState<SearchHit[]>([])
  const [tlLoading, setTlLoading] = useState(true)

  // recent emotion logs
  const [recentLogs, setRecentLogs] = useState<MindEmotionLog[]>([])
  const [showAllLogs, setShowAllLogs] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setTlLoading(true)
    Promise.all([
      apiFetch<SearchResponse>('/api/search', {
        query: { domain: 'mind', domain_mode: 'broad', sort: '-created_at', limit: 50 },
        signal: ac.signal,
      }).catch(() => null),
      listOperationalData<MindEmotionLog>(DATASET, { limit: 20 }).catch(() => null),
    ]).then(([tl, logs]) => {
      setTimeline(tl?.entries ?? [])
      setRecentLogs(logs?.rows ?? [])
      setTlLoading(false)
    })
    return () => ac.abort()
  }, [reload])

  function fillFromLog(row: MindEmotionLog) {
    setEditingId(row.log_id)
    setObservedAt(row.observed_at.slice(0, 16))
    setEmotion(row.emotion)
    setIntensity(row.intensity?.toString() ?? '')
    setTrigger(row.trigger ?? '')
    setBodySensation(row.body_sensation ?? '')
    setThought(row.thought ?? '')
    setContext(row.context ?? '')
    if (viewport === 'mobile') {
      setShowDetails(true)
      window.scrollTo(0, 0)
    }
  }

  function resetForm() {
    setEditingId(null); setObservedAt(nowLocal()); setEmotion('calm')
    setIntensity('5'); setTrigger(''); setBodySensation('')
    setThought(''); setContext(''); setSubmitMsg(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true); setSubmitMsg(null)
    try {
      const log_id = editingId ?? uuidv4()
      const observed = observedAt.length === 16 ? new Date(observedAt).toISOString() : observedAt
      const body: Record<string, unknown> = { log_id, observed_at: observed, emotion, source: 'cockpit' }
      if (intensity) body.intensity = parseInt(intensity, 10)
      if (trigger) body.trigger = trigger
      if (bodySensation) body.body_sensation = bodySensation
      if (thought) body.thought = thought
      if (context) body.context = context
      await upsertOperationalData<MindEmotionLog>(DATASET, body)
      setSubmitMsg(editingId ? `更新しました` : `記録しました`)
      setReload(n => n + 1)
      if (!editingId) resetForm()
    } catch (e: unknown) {
      setSubmitMsg(`保存失敗: ${e instanceof Error ? e.message : String(e)}`)
    } finally { setSubmitting(false) }
  }

  const inp = 'px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm text-text-base placeholder:text-text-subtle focus:outline-none focus:border-border-strong'

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto px-8 py-10 space-y-10">
        <header>
          <p className="text-xs uppercase tracking-wide text-text-subtle">Mind</p>
          <h1 className="text-2xl font-medium text-text-base mt-1">Emotion Log</h1>
          <p className="text-xs text-text-muted mt-1">感情ログは「今こう感じた」の即時記録です。後から否定しない。</p>
        </header>

        {/* 入力フォーム */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-4">
            {editingId ? `Edit (${editingId.slice(0, 8)})` : 'New Record'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Primary fields: 常に表示 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-text-subtle">観察時刻 *</span>
                <input type="datetime-local" value={observedAt} onChange={e => setObservedAt(e.target.value)} required className={inp} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-text-subtle">感情 *</span>
                <select value={emotion} onChange={e => setEmotion(e.target.value)} required
                  className={`${inp} bg-bg-base`}>
                  {EMOTIONS.map(em => <option key={em} value={em}>{em}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-text-subtle">強度 (1-10)</span>
                <input type="number" min="1" max="10" value={intensity} onChange={e => setIntensity(e.target.value)} placeholder="5" className={inp} />
              </label>
            </div>

            {/* モバイル: 詳細展開ボタン */}
            {viewport === 'mobile' && (
              <button type="button" onClick={() => setShowDetails(s => !s)}
                className="text-sm text-accent-primary hover:underline text-left">
                {showDetails ? '▲ 詳細を閉じる' : '▼ 詳細を入力 (trigger / 身体感覚 / thought / context)'}
              </button>
            )}

            {/* Secondary fields: デスクトップは常に表示、モバイルは展開時のみ */}
            {(viewport !== 'mobile' || showDetails) && (
              <>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">trigger (出来事・引き金)</span>
                  <input type="text" value={trigger} onChange={e => setTrigger(e.target.value)}
                    placeholder="会議でフィードバックを受けた / 朝のニュース 等" className={inp} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">body_sensation (身体感覚)</span>
                  <input type="text" value={bodySensation} onChange={e => setBodySensation(e.target.value)}
                    placeholder="胸が締め付けられる / 肩が緊張する 等" className={inp} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">thought (浮かんだ考え)</span>
                  <textarea value={thought} onChange={e => setThought(e.target.value)} rows={2}
                    placeholder="自分が責められていると感じた 等"
                    className={`${inp} resize-none`} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">context (状況)</span>
                  <input type="text" value={context} onChange={e => setContext(e.target.value)}
                    placeholder="after meeting / morning commute 等" className={inp} />
                </label>
              </>
            )}
            <div className="flex items-center gap-3">
              <button type="submit" disabled={submitting}
                className="px-4 py-1.5 bg-accent-primary text-bg-surface rounded text-sm hover:opacity-90 disabled:opacity-60">
                {submitting ? '保存中…' : (editingId ? '更新' : '記録')}
              </button>
              <button type="button" onClick={resetForm} disabled={submitting}
                className="px-4 py-1.5 border border-border-subtle rounded text-sm text-text-muted hover:bg-bg-overlay">
                {editingId ? '編集キャンセル' : 'クリア'}
              </button>
              {submitMsg && <span className="text-sm text-text-subtle">{submitMsg}</span>}
            </div>
          </form>
        </section>

        {/* Recent Emotion Logs (Operational Data) */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xs uppercase tracking-wide text-text-subtle">Recent Logs</h2>
            {recentLogs.length > 3 && (
              <button type="button" onClick={() => setShowAllLogs(s => !s)}
                className="text-xs text-accent-primary hover:underline">
                {showAllLogs ? '▲ 閉じる' : `▼ すべて見る (${recentLogs.length})`}
              </button>
            )}
          </div>
          {recentLogs.length === 0 ? (
            <p className="text-sm text-text-muted">まだログがありません</p>
          ) : (
            <ul className="space-y-1.5">
              {(showAllLogs ? recentLogs : recentLogs.slice(0, 3)).map(row => (
                <li key={row.log_id}>
                  <button type="button" onClick={() => fillFromLog(row)}
                    className="w-full text-left px-3 py-2 rounded border border-border-subtle hover:bg-bg-overlay transition">
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${emotionColor(row.emotion)}`}>
                        {row.emotion}
                      </span>
                      {row.intensity != null && <span className="text-text-subtle">{row.intensity}/10</span>}
                      <span className="ml-auto text-text-subtle font-mono text-[11px]">{fmtDateTime(row.observed_at)}</span>
                    </div>
                    {row.trigger && <p className="text-[11px] text-text-muted line-clamp-1 mt-0.5">{row.trigger}</p>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* mind entries タイムライン */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-4">Mind Entries</h2>
          {tlLoading ? (
            <p className="text-sm text-text-subtle">Loading…</p>
          ) : timeline.length === 0 ? (
            <EmptyState title="No mind entries" description="mind domain の Entry がありません" />
          ) : (
            <ul className="border-t border-border-subtle">
              {timeline.map(hit => {
                const itemType: 'entry' | 'article' = hit.origin === 'synthesized' ? 'article' : 'entry'
                return (
                  <li key={hit.id}>
                    <ResultRow
                      id={hit.id}
                      title={hit.title}
                      one_line={hit.one_line}
                      origin={hit.origin}
                      subjectivity={hit.subjectivity}
                      source_project={hit.source_project ?? null}
                      domains={hit.domains ?? []}
                      visibility={hit.visibility ?? null}
                      has_decision={hit.has_decision}
                      x_status={hit.x_status ?? null}
                      created_at={hit.created_at}
                      selected={selectedItem?.id === hit.id}
                      onClick={() => setSelectedItem({ id: hit.id, itemType })}
                    />
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
